import { createInstance, OptimizelyDecideOption, setLogLevel } from "@optimizely/optimizely-sdk";
const DEFAULT_TIMEOUT = 2000;
export function getNullDecision(flagKey, variationOverride) {
    return {
        variationKey: variationOverride || 'off',
        enabled: variationOverride ? variationOverride !== 'off' : false,
        variables: {},
        ruleKey: null,
        flagKey,
        userContext: undefined,
        reasons: []
    };
}
export class NullProject {
    async decision(flagKey, _userId, options) {
        return getNullDecision(flagKey, options?.variationOverride);
    }
    async close() {
        // no-op
    }
    getOptimizelyProject() {
        throw new Error("[Signalmizely] Cannot access Optimizely client in null mode");
    }
}
export class Project {
    instance;
    constructor(instance) {
        this.instance = instance;
    }
    static async initialize(params) {
        try {
            if (params.options?.logLevel) {
                setLogLevel(params.options.logLevel);
            }
            let instance;
            if (params.datafile) {
                instance = createInstance({
                    datafile: params.datafile,
                });
            }
            else {
                instance = createInstance({
                    sdkKey: params.sdkKey,
                    ...(params.options && {
                        datafileOptions: {
                            autoUpdate: params.options.autoUpdate,
                            updateInterval: params.options.updateInterval,
                        }
                    })
                });
            }
            if (!instance) {
                console.warn("[Signalmizely] Failed to create Optimizely instance, using null project");
                return new NullProject();
            }
            // Wait for the datafile to be ready before returning the Project
            const result = await instance.onReady({ timeout: DEFAULT_TIMEOUT });
            if (!result.success) {
                console.warn(`[Signalmizely] Failed to initialize Optimizely (${result.reason}), using null project`);
                return new NullProject();
            }
            return new Project(instance);
        }
        catch (error) {
            console.warn("[Signalmizely] Error during initialization, using null project:", error);
            return new NullProject();
        }
    }
    async decision(flagKey, userId, options) {
        const userContext = await this.getUserContext(userId, options?.attributes);
        // Set forced decision if variation override is provided
        if (options?.variationOverride) {
            const decisionContext = this.getDecisionContext(flagKey);
            const forcedDecision = this.getForcedDecision(options.variationOverride);
            userContext.setForcedDecision(decisionContext, forcedDecision);
        }
        // Disable decision event if variation is forced or sendImpression is false
        const decisionOptions = [];
        // Default to true if sendImpression is not provided
        const sendImpression = options?.sendImpression ?? true;
        if (options?.variationOverride || !sendImpression) {
            decisionOptions.push(OptimizelyDecideOption.DISABLE_DECISION_EVENT);
        }
        return userContext.decide(flagKey, decisionOptions);
    }
    async close() {
        await this.instance.close();
    }
    getOptimizelyProject() {
        return this.instance;
    }
    // private
    async getUserContext(userId, attributes) {
        const userContext = this.instance.createUserContext(userId, attributes);
        if (!userContext)
            throw new Error("Failed to create user context");
        return userContext;
    }
    getDecisionContext(flagKey) {
        return { flagKey };
    }
    getForcedDecision(variationOverride) {
        return { variationKey: variationOverride };
    }
}
